/**
 * ScenarioPreviewDrawer — therapist-side scenario brief preview.
 *
 * Renders the scenario the therapist is about to practice, honouring
 * `visibilityLevel`:
 *   full_brief    — presenting complaint, background, referral, objectives, assessments
 *   referral_only — referral summary + presenting line only
 *   blind         — minimal: title + "blind assessment" notice + objectives
 *
 * The footer "Start practice" button proxies to the Lab's start handler so
 * we don't fork the kick-off logic.
 */

import React from 'react';
import {
  Drawer, Box, Typography, Stack, Chip, Button, Divider, Alert, IconButton,
  CircularProgress, List, ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const Section = ({ title, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

export default function ScenarioPreviewDrawer({
  scenario,
  open,
  onClose,
  onStart,
  starting,
  rubricName,
}) {
  if (!scenario) return null;
  const visibilityLevel = scenario.visibilityLevel || 'full_brief';
  const ctx = scenario.scenarioContext || {};
  const objectives = (scenario.learningObjectives || []).filter(Boolean);
  const assessments = scenario.availableAssessments || [];

  const isBlind = visibilityLevel === 'blind';
  const isReferralOnly = visibilityLevel === 'referral_only';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Sit below the app bar (56px on mobile, 64px on desktop) so the
      // drawer header isn't hidden behind the AIMHI Platform top bar.
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 520 },
          top: { xs: 56, sm: 64 },
          height: { xs: 'calc(100% - 56px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Persona portrait — always shown when present. Blind mode means
            the therapist receives no referral / brief, but they still see
            the patient sitting across from them. */}
        {scenario.personaImageUrl && (
          <Box
            sx={{
              width: '100%',
              height: 220,
              backgroundImage: `url(${scenario.personaImageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundColor: 'grey.100',
              flexShrink: 0,
            }}
          />
        )}

        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight={600}>{scenario.name}</Typography>
              <Stack direction="row" spacing={0.5} sx={{ mt: 1, gap: 0.5 }} flexWrap="wrap">
                {scenario.difficulty && (
                  <Chip size="small" label={scenario.difficulty} variant="outlined" sx={{ textTransform: 'capitalize' }} />
                )}
                {scenario.category && (
                  <Chip size="small" label={(scenario.category || '').replace(/_/g, ' ')} variant="outlined" />
                )}
                {ctx.setting && (
                  <Chip size="small" label={(ctx.setting || '').replace(/_/g, ' ')} variant="outlined" />
                )}
                {ctx.timeLimit && (
                  <Chip size="small" icon={<AccessTimeIcon />} label={`${ctx.timeLimit} min`} variant="outlined" />
                )}
              </Stack>
            </Box>
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Stack>

          {/* Visibility banner */}
          {isBlind && (
            <Alert severity="info" icon={<VisibilityOffIcon />} sx={{ mt: 2 }}>
              <strong>Blind assessment</strong> — you'll begin without prior information about this patient.
              Lead the conversation and arrive at your own clinical impression.
            </Alert>
          )}
          {isReferralOnly && (
            <Alert severity="info" icon={<DescriptionIcon />} sx={{ mt: 2 }}>
              <strong>Referral-only mode</strong> — you have a brief referral note; the rest is yours to elicit.
            </Alert>
          )}
        </Box>

        {/* Body */}
        <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
          {/* Description (only when not blind) */}
          {!isBlind && scenario.description && (
            <Section title="About">
              <Typography variant="body2">{scenario.description}</Typography>
            </Section>
          )}

          {/* Referral / brief */}
          {(isReferralOnly || isBlind) && scenario.referralSummary && (
            <Section title="Referral">
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                {scenario.referralSummary}
              </Typography>
            </Section>
          )}
          {!isBlind && !isReferralOnly && ctx.referralSource && (
            <Section title="Referral source">
              <Typography variant="body2">{ctx.referralSource}</Typography>
            </Section>
          )}

          {/* Presenting complaint — full brief only */}
          {!isBlind && !isReferralOnly && ctx.presentingComplaint && (
            <Section title="Presenting complaint">
              <Typography variant="body2">{ctx.presentingComplaint}</Typography>
            </Section>
          )}

          {/* Background — full brief only */}
          {!isBlind && !isReferralOnly && ctx.backgroundBrief && (
            <Section title="Background">
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {ctx.backgroundBrief}
              </Typography>
            </Section>
          )}

          {/* Objectives */}
          {objectives.length > 0 && (
            <Section title="Learning objectives">
              <List dense disablePadding>
                {objectives.map((o, i) => (
                  <ListItem key={i} disableGutters sx={{ alignItems: 'flex-start', py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                      <CheckCircleOutlineIcon fontSize="small" color="action" />
                    </ListItemIcon>
                    <ListItemText primary={<Typography variant="body2">{o}</Typography>} />
                  </ListItem>
                ))}
              </List>
            </Section>
          )}

          {/* Available assessments — hidden in blind+hidden mode */}
          {assessments.length > 0 && !(isBlind && scenario.blindModeAssessments === 'hidden') && (
            <Section title="Available assessments">
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                {assessments.map(a => (
                  <Chip key={a} icon={<AssignmentIcon />} label={a.toUpperCase ? a.toUpperCase() : a} size="small" variant="outlined" />
                ))}
              </Stack>
              {isBlind && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  You may request any of these during the session.
                </Typography>
              )}
            </Section>
          )}

          {/* Rubric */}
          {rubricName && (
            <Section title="Evaluation rubric">
              <Typography variant="body2">{rubricName}</Typography>
            </Section>
          )}

          {!isBlind && !ctx.presentingComplaint && !ctx.backgroundBrief && objectives.length === 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              This scenario has limited brief information. You may want to ask the author to populate the
              presenting complaint and learning objectives.
            </Alert>
          )}
        </Box>

        {/* Footer */}
        <Divider />
        <Box sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={starting ? <CircularProgress size={14} /> : <PlayArrowIcon />}
            onClick={() => onStart(String(scenario._id))}
            disabled={!!starting}
            size="large"
          >
            {starting ? 'Starting…' : 'Start practice'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
